import {inject, Injectable} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {NavigationStart, Router} from "@angular/router";
import {BehaviorSubject, filter, finalize} from "rxjs";
import {Organization, OrganizationData} from "../interfaces/organization";
import {BaseService} from "./base.service";
import {CookieService} from "./cookie.service";

@Injectable({
  providedIn: 'root'
})
export class OrganizationsService extends BaseService {
  private http = inject(HttpClient);
  private cookie = inject(CookieService);

  public organizations$ = new BehaviorSubject<Organization[]>([]);
  public buEnabled: boolean = true;
  // Чи завершився хоч один запит списку (успіх, помилка або скасування). До цього порожній
  // organizations$ означає «ще не знаємо», а не «організацій немає» (картка #388).
  public loaded$ = new BehaviorSubject<boolean>(false);
  // Акаунт (куки входу), для якого отримано список. Перемикання акаунта і вихід у цій вкладці
  // перезавантажують сторінку, але акаунт можуть змінити і без цього: в іншій вкладці («Дозволені
  // для входу», «дивитись як», вихід/вхід на новому сайті). Тоді до приходу нового списку шапка
  // й кошик показували організацію попереднього акаунта (картка #416)
  private loadedForAccount: string = null;

  constructor() {
    super();

    // Звіряємо на початку кожного переходу - до того, як нова сторінка (кошик, шапка) прочитає список
    inject(Router).events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe(() => {
        if (this.loadedForAccount !== null && this.loadedForAccount !== this.currentAccount()) {
          this.reset();
          // Перечитуємо одразу: при переході в межах тієї ж сторінки (/good/1 → /good/2) шапка не
          // створюється заново, і без цього список лишився б порожнім
          this.getOrganizations();
        }
      });
  }

  getOrganizations() {
    const account = this.currentAccount();

    this.http.post(this.apiRoot + 'organizations/get_all/', {})
      .pipe(finalize(() => this.loaded$.next(true)))
      .subscribe((data: OrganizationData) => {
        this.loadedForAccount = account;
        // buEnabled - до next(): підписники (особисті дані, меню) читають його синхронно в обробнику списку,
        // інакше після перезавантаження сторінки бачили значення за замовчуванням true
        this.buEnabled = data.bu_enabled;
        this.organizations$.next(data.data || []);
      });
  }

  // Список бек будує для userAsAdmin: акаунт входу (кука email) або підмінений через «дивитись як»
  private currentAccount() {
    return this.cookie.getCookie('email') + '|' + this.cookie.getCookie('admin_goods_look_like_user');
  }

  private reset() {
    this.loadedForAccount = null;
    this.buEnabled = true;
    this.loaded$.next(false);
    this.organizations$.next([]);
  }

  createOrganization(organization: Organization) {
    return this.http.post(this.apiRoot + 'organizations/create/', organization);
  }

  updateOrganization(organization: Organization) {
    return this.http.post(this.apiRoot + 'organizations/update/', organization);
  }

  deleteOrganization(id: number | string) {
    return this.http.post(this.apiRoot + 'organizations/delete/', {id});
  }

  setActiveOrganization(id: number | string) {
    return this.http.post(this.apiRoot + 'organizations/set_active/', {id});
  }
}
